'use client'

import { Accordion, Avatar, Box, Button, Drawer, Portal, Separator, Flex, Text } from '@chakra-ui/react'
import { Category } from 'app/utils/helpers/helpers'
import { getCategoryName } from 'components/nav/CategoriesMenu/utils/categories'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { LuX } from 'react-icons/lu'

const getCategoryDisplayName = (category: Category): string => {
  return getCategoryName(category)
}

type Props = {
  categoriesToShow: Category[]
  handleSelectCategory: (category: Category) => void
}

// Helper function to check if any child category matches current ID
const hasMatchingChild = (category: Category, currentId: number | undefined): boolean => {
  if (!currentId || !category.subCategory) return false
  return category.subCategory.some((child) => child.id === currentId || hasMatchingChild(child, currentId))
}

// Recursive category renderer
const CategoryItem = ({
  category,
  level = 0,
  path,
  handleSelectCategory,
}: {
  category: Category
  level?: number
  path: string
  handleSelectCategory: (category: Category) => void
}) => {
  const { t } = useTranslation('common')
  const paddingLeft = `${level * 20}px`
  const keyBase = `${path}-${category.id}`
  const currentUrl = window.location.href
  const currentCategoryId = currentUrl.split('browse').pop()?.split('-')?.pop()
  const currentCategoryIdNumber = currentCategoryId ? parseInt(currentCategoryId, 10) : undefined

  if (!category.subCategory || category.subCategory.length === 0) {
    return (
      <Flex
        as="li"
        key={keyBase}
        alignItems="center"
        justifyContent="start"
        gap={2}
        w="100%"
        padding="10px 10px"
        paddingLeft={paddingLeft === '0px' ? '10px' : paddingLeft}
        onClick={() => handleSelectCategory(category)}
        cursor="pointer"
        _hover={{ bg: 'gray.50' }}
        backgroundColor={currentCategoryIdNumber === Number(category.id) ? 'green.a25' : 'transparent'}
      >
        <Avatar.Root shape="rounded">
          <Avatar.Image src={category.imageUrl} />
          <Avatar.Fallback name={getCategoryDisplayName(category)} />
        </Avatar.Root>
        <Text>{getCategoryDisplayName(category)}</Text>
      </Flex>
    )
  }

  // With subcategories → Accordion
  const shouldBeOpen = hasMatchingChild(category, currentCategoryIdNumber)
  return (
    <Accordion.Root key={keyBase} collapsible variant="subtle" defaultValue={shouldBeOpen ? [keyBase] : []}>
      <Accordion.Item value={keyBase} backgroundColor="transparent">
        <Accordion.ItemTrigger
          style={{ padding: '10px 10px' }}
          justifyContent="space-between"
          gap={2}
          paddingLeft={paddingLeft}
          backgroundColor={currentCategoryIdNumber === category.id ? 'green.a25' : 'transparent'}
        >
          <Box display="flex" alignItems="center" justifyContent="start" gap={2}>
            <Avatar.Root shape="rounded">
              <Avatar.Image src={category.imageUrl} />
              <Avatar.Fallback name={getCategoryDisplayName(category)} />
            </Avatar.Root>
            <Text fontSize="sm" fontWeight="normal">
              {getCategoryDisplayName(category)}
            </Text>
          </Box>
          <Accordion.ItemIndicator />
        </Accordion.ItemTrigger>

        <Accordion.ItemContent marginBottom="4px" paddingRight="0px">
          <Accordion.ItemBody>
            <Box as="ul" width="100%">
              <Flex
                key={`${keyBase}-all`}
                alignItems="center"
                justifyContent="start"
                gap={2}
                w="100%"
                padding="10px 2px"
                paddingLeft={`${(level + 1) * 20}px`}
                cursor="pointer"
                _hover={{ bg: 'gray.50' }}
                onClick={() => handleSelectCategory(category)}
                backgroundColor={currentCategoryIdNumber === category.id ? 'green.a25' : 'transparent'}
              >
                <Avatar.Root shape="rounded">
                  <Avatar.Image src={category.imageUrl} />
                  <Avatar.Fallback name={getCategoryDisplayName(category)} />
                </Avatar.Root>
                <Text>
                  {t('all')} {getCategoryDisplayName(category)}
                </Text>
              </Flex>

              {category.subCategory.map((child: Category, childIndex: number) => (
                <CategoryItem
                  key={`${keyBase}-child-${childIndex}`}
                  category={child}
                  level={level + 1}
                  path={`${keyBase}-child-${childIndex}`}
                  handleSelectCategory={handleSelectCategory}
                />
              ))}
            </Box>
          </Accordion.ItemBody>
        </Accordion.ItemContent>
      </Accordion.Item>
    </Accordion.Root>
  )
}

const CategoryDrawer = ({ categoriesToShow, handleSelectCategory }: Props) => {
  const [open, setOpen] = useState(false)
  const { t } = useTranslation('common')

  return (
    <Drawer.Root open={open} onOpenChange={(e) => setOpen(e.open)} size={{ base: 'md', smDown: 'full' }}>
      <Drawer.Trigger asChild>
        <Button
          variant="outline"
          size="sm"
          backgroundColor="green.a200"
          padding="20px 12px"
          borderRadius="md"
          color="white"
          border="none"
        >
          {t('selectCategory')}
        </Button>
      </Drawer.Trigger>

      <Portal>
        <Drawer.Backdrop />
        <Drawer.Positioner>
          <Drawer.Content>
            <Drawer.Header marginTop="40px">
              <Drawer.Title>{t('selectCategory')}</Drawer.Title>
              <Separator marginY="20px" />
            </Drawer.Header>

            <Drawer.Body>
              <Box as="ul" width="100%">
                {categoriesToShow.map((category, index) => (
                  <CategoryItem
                    key={`root-${index}`}
                    category={category}
                    path={`root-${index}`}
                    handleSelectCategory={handleSelectCategory}
                  />
                ))}
              </Box>
            </Drawer.Body>

            <Drawer.CloseTrigger asChild marginTop="18px" marginRight="18px">
              <LuX size={24} />
            </Drawer.CloseTrigger>
          </Drawer.Content>
        </Drawer.Positioner>
      </Portal>
    </Drawer.Root>
  )
}

export default CategoryDrawer
